import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star, TrendingUp, Sparkles, Check, ArrowRight } from "lucide-react";
import { brand } from "@/config/brand";
import { scrollToPricing, scrollToId } from "@/lib/scroll";
import { MaskedLines } from "@/components/primitives";
import { assets } from "@/config/assets";

const Stars = ({ n = 5, size = 12 }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: n }).map((_, i) => (
      <Star key={i} size={size} className="fill-black text-black" />
    ))}
  </div>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yUp = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const yDown = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative overflow-hidden bg-base pt-[72px]"
    >
      <div className="pointer-events-none absolute inset-0 bg-grid opacity-60 [mask-image:radial-gradient(ellipse_at_top,black_30%,transparent_75%)]" />
      <div className="noise" />

      <div className="relative mx-auto grid max-w-[1280px] grid-cols-1 items-center gap-16 px-5 pb-24 pt-16 sm:px-8 lg:grid-cols-[1.05fr_1fr] lg:gap-10 lg:pb-32 lg:pt-24">
        {/* Left: copy */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-7 inline-flex items-center gap-2 rounded-[10px] border border-black/10 bg-white px-3.5 py-1.5 text-xs font-medium text-black/70"
          >
            <span className="flex h-1.5 w-1.5 rounded-full bg-black" />
            The AI growth platform for local business
          </motion.div>

          <h1 className="text-[2.6rem] font-black leading-[0.98] tracking-tighter text-black sm:text-6xl lg:text-[4.5rem]">
            <MaskedLines
              lines={["Get More Reviews,", "More Visibility, and", "More Local Customers"]}
              delay={0.15}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-7 max-w-xl text-base font-light leading-relaxed text-black/60 sm:text-lg"
          >
            <span className="font-medium text-black">{brand.brand_name}</span> helps
            local businesses collect reviews, improve Google visibility, and turn
            customer feedback into growth with AI-powered automation.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-9 flex flex-col gap-3 sm:flex-row"
          >
            <button
              data-testid="hero-primary-cta"
              onClick={scrollToPricing}
              className="group inline-flex items-center justify-center gap-2 rounded-[10px] bg-black px-7 py-4 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5"
            >
              Get Started
              <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-secondary-cta"
              onClick={() => scrollToId("how")}
              className="inline-flex items-center justify-center gap-2 rounded-[10px] border border-black/20 bg-transparent px-7 py-4 text-sm font-semibold text-black transition-colors duration-200 hover:border-black hover:bg-black/[0.03]"
            >
              See How It Works
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-10 flex items-center gap-4"
          >
            <div className="flex -space-x-2">
              {[assets.agents.reply, assets.agents.share, assets.agents.geo].map((src) => (
                <img
                  key={src}
                  src={src}
                  alt=""
                  className="h-8 w-8 rounded-full border-2 border-base object-cover"
                />
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Stars n={5} />
              <span className="text-xs font-medium text-black/60">
                Loved by modern local businesses
              </span>
            </div>
          </motion.div>
        </div>

        {/* Right: product composite */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto w-full max-w-[560px]"
        >
          {/* Lifestyle frame */}
          <motion.div style={{ y: yDown }} className="relative">
            <div className="overflow-hidden rounded-[10px] border border-black/10 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.45)]">
              <img
                src={assets.lifestyle.heroOwner}
                alt="Local business owner"
                className="h-[300px] w-full object-cover sm:h-[360px]"
              />
            </div>
          </motion.div>

          {/* Dashboard card (floating, overlapping) */}
          <motion.div
            style={{ y: yUp }}
            className="absolute -bottom-10 -left-4 w-[290px] rounded-[10px] border border-black/10 bg-white p-4 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.4)] sm:-left-10 sm:w-[320px]"
          >
            <div className="flex items-center justify-between border-b border-black/[0.06] pb-3">
              <div className="flex items-center gap-2">
                <div className="flex h-6 w-6 items-center justify-center rounded-[6px] bg-black">
                  <Star size={12} className="fill-white text-white" />
                </div>
                <span className="text-xs font-semibold">Reputation</span>
              </div>
              <span className="inline-flex items-center gap-1 rounded-full bg-black/[0.05] px-2 py-0.5 text-[10px] font-medium text-black/60">
                <TrendingUp size={10} /> +38%
              </span>
            </div>
            <div className="flex items-end justify-between pt-3">
              <div>
                <div className="text-3xl font-black tracking-tight">4.9</div>
                <div className="mt-0.5"><Stars n={5} size={11} /></div>
              </div>
              <div className="flex h-14 items-end gap-1">
                {[30, 45, 38, 60, 52, 78, 90].map((h, i) => (
                  <motion.span
                    key={i}
                    initial={{ height: 0 }}
                    animate={{ height: `${h}%` }}
                    transition={{ duration: 0.7, delay: 0.8 + i * 0.06 }}
                    className="w-2.5 rounded-[3px] bg-black/85"
                  />
                ))}
              </div>
            </div>
            <div className="mt-3 border-t border-black/[0.06] pt-2 text-[11px] font-medium text-black/50">
              1,284 reviews collected this quarter
            </div>
          </motion.div>

          {/* AI assistant preview chip (ai_name exists, logo/prompt null -> hidden) */}
          {brand.ai_settings?.ai_name && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 1.1 }}
              className="absolute -right-3 top-6 w-[210px] rounded-[10px] border border-black/10 bg-white/90 p-3 shadow-[0_20px_50px_-25px_rgba(0,0,0,0.4)] backdrop-blur-md sm:-right-8"
            >
              <div className="flex items-center gap-2">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-black">
                  <Sparkles size={12} className="text-white" />
                </div>
                <span className="text-xs font-semibold">{brand.ai_settings.ai_name}</span>
                <span className="ml-auto flex gap-0.5">
                  {[0, 1, 2].map((i) => (
                    <motion.span
                      key={i}
                      className="h-1 w-1 rounded-full bg-black/40"
                      animate={{ opacity: [0.2, 1, 0.2] }}
                      transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                    />
                  ))}
                </span>
              </div>
              <p className="mt-2 text-[11px] leading-snug text-black/60">
                Drafted a reply to a new 5-star review and scheduled a social post.
              </p>
              <div className="mt-2 inline-flex items-center gap-1 rounded-[6px] bg-black/[0.05] px-2 py-0.5 text-[10px] font-medium text-black/60">
                <Check size={10} /> Auto-generated
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
